// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { CASE_DUPLICATE_HASH_LABELER_FAMILY } from './CaseBuddyAppConstants.js';
import { abbreviateCaseBuddyHash, caseBuddyHashString, firstNonEmptyEntryValue, normalizeCell } from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export function duplicateHashNormalizeHash(value = '') {
  const text = normalizeCell(value);
  if (!text) return '';
  return text.toLowerCase();
}

export function duplicateHashNormalizeLabel(value = '') {
  const text = normalizeCell(value);
  return text || '(unlabeled)';
}

export function duplicateHashNaturalTokens(value = '') {
  return String(value || '').match(/\d+|\D+/g) || [''];
}

export function duplicateHashNaturalCompare(left = '', right = '') {
  const a = duplicateHashNaturalTokens(left);
  const b = duplicateHashNaturalTokens(right);
  const length = Math.max(a.length, b.length);

  for (let index = 0; index < length; index += 1) {
    if (a[index] === undefined) return -1;
    if (b[index] === undefined) return 1;

    const aIsNumber = /^\d+$/.test(a[index]);
    const bIsNumber = /^\d+$/.test(b[index]);
    if (aIsNumber && bIsNumber) {
      const diff = Number(a[index]) - Number(b[index]);
      if (diff !== 0) return diff;
    } else {
      const diff = String(a[index]).localeCompare(String(b[index]), undefined, { sensitivity: 'base' });
      if (diff !== 0) return diff;
    }
  }

  return 0;
}

export function duplicateHashFormatLabelList(labels = []) {
  const list = (Array.isArray(labels) ? labels : []).map(duplicateHashNormalizeLabel).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

const CASE_DUPLICATE_HASH_LABEL_TOKEN_PATTERN = String.raw`(?:\([^)]+\)|[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)`;
const CASE_DUPLICATE_HASH_ANNOTATION_PATTERN = String.raw`\s*\bThis transaction\s*\([^)]+\)\s+is the same transaction hash as\s+${CASE_DUPLICATE_HASH_LABEL_TOKEN_PATTERN}(?:\s*,\s*${CASE_DUPLICATE_HASH_LABEL_TOKEN_PATTERN})*(?:\s+and\s+${CASE_DUPLICATE_HASH_LABEL_TOKEN_PATTERN})?\.`;
const CASE_DUPLICATE_HASH_ANNOTATION_REGEX = new RegExp(CASE_DUPLICATE_HASH_ANNOTATION_PATTERN, 'gi');

function cleanCaseDuplicateHashNoteSpacing(notes = '') {
  return String(notes || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map(line => String(line || '')
      .replace(/[ \t]+/g, ' ')
      .replace(/\s+([.,;:])/g, '$1')
      .trim())
    .filter(Boolean)
    .join('\n')
    .trim();
}

export function stripCaseDuplicateHashNotes(notes = '') {
  const text = String(notes || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!text) return '';

  // Legacy Tool63B/C annotation forms and the newer plain-language form are stripped
  // surgically. Do not split on periods and drop any fragment containing
  // "This transaction": swap/bridge notes often append the duplicate sentence
  // directly after an important note without punctuation, and labels such as 2d1.1
  // contain periods of their own.
  return cleanCaseDuplicateHashNoteSpacing(
    text
      .replace(/\s*\[Tool63[BC]\][^\n\r]*/gi, ' ')
      .replace(CASE_DUPLICATE_HASH_ANNOTATION_REGEX, ' ')
  );
}

export function appendCaseDuplicateHashAnnotation(baseNotes = '', annotation = '') {
  const base = cleanCaseDuplicateHashNoteSpacing(baseNotes);
  const note = normalizeCell(annotation);
  if (!note) return base;
  if (!base) return note;
  const separator = /[.!?]$/.test(base) ? ' ' : '. ';
  return `${base}${separator}${note}`;
}

function normalizeCaseDuplicateHashBaseForSafetyCompare(notes = '') {
  return normalizeCell(stripCaseDuplicateHashNotes(notes))
    .replace(/\s+/g, ' ')
    .replace(/[.!?]+$/g, '')
    .trim();
}

export function caseDuplicateHashProposalPreservesNonDuplicateNotes(existingNotes = '', proposedNotes = '') {
  return normalizeCaseDuplicateHashBaseForSafetyCompare(existingNotes) === normalizeCaseDuplicateHashBaseForSafetyCompare(proposedNotes);
}

export function hasCaseDuplicateHashNote(notes = '') {
  const text = String(notes || '');
  return /\[Tool63[BC]\]/i.test(text) || /This transaction \([^)]+\) is the same transaction hash as /i.test(text);
}

export function buildCaseDuplicateHashAnnotation(currentLabel = '', otherLabels = []) {
  const label = duplicateHashNormalizeLabel(currentLabel);
  const others = (Array.isArray(otherLabels) ? otherLabels : [])
    .map(duplicateHashNormalizeLabel)
    .filter(item => item && item !== label)
    .sort(duplicateHashNaturalCompare);
  if (others.length === 0) return '';
  return `This transaction (${label}) is the same transaction hash as ${duplicateHashFormatLabelList(others)}.`;
}

export function buildCaseDuplicateHashProposedNotes(existingNotes = '', currentLabel = '', otherLabels = []) {
  const baseNotes = stripCaseDuplicateHashNotes(existingNotes);
  const annotation = buildCaseDuplicateHashAnnotation(currentLabel, otherLabels);
  return appendCaseDuplicateHashAnnotation(baseNotes, annotation);
}

export function buildCaseDuplicateHashReviewKey(caseId = '', rowIndex = 0, transactionHash = '', pathLabel = '', proposedNotes = '') {
  const payload = [
    CASE_DUPLICATE_HASH_LABELER_FAMILY,
    normalizeCell(caseId) || 'case',
    rowIndex,
    duplicateHashNormalizeHash(transactionHash),
    duplicateHashNormalizeLabel(pathLabel),
    caseBuddyHashString(proposedNotes || '')
  ];
  return payload.join('|');
}

export function buildCaseDuplicateHashEntryRecords(mainData = []) {
  const data = normalizeCaseMatrix(mainData);
  const entries = parseCaseEntries(data);
  return entries
    .filter(entry => entry?.hash && Array.isArray(entry.rows) && entry.rows.length > 0)
    .map((entry) => {
      const lead = entry.rows[0] || { rowIndex: entry.startRow, row: [] };
      const leadRow = Array.isArray(lead.row) ? lead.row : [];
      const systemNotes = normalizeCell(leadRow?.[9]);
      const pathLabel = duplicateHashNormalizeLabel(firstNonEmptyEntryValue(entry.rows, 0) || leadRow?.[0]);
      const transactionHash = normalizeCell(entry.hash || leadRow?.[1]);
      const rowValues = Array.isArray(entry.rows) ? entry.rows.map(item => Array.isArray(item.row) ? item.row : []) : [];

      return {
        rowIndex: lead.rowIndex,
        rowNumber: lead.rowIndex + 1,
        transactionHash,
        hashKey: duplicateHashNormalizeHash(transactionHash),
        pathLabel,
        systemNotes,
        strippedNotes: stripCaseDuplicateHashNotes(systemNotes),
        hasOldDuplicateNote: hasCaseDuplicateHashNote(systemNotes),
        attribution: firstNonEmptyEntryValue(entry.rows, 10) || normalizeCell(leadRow?.[10]),
        analystNotes: firstNonEmptyEntryValue(entry.rows, 11) || normalizeCell(leadRow?.[11]),
        fromHash: firstNonEmptyEntryValue(entry.rows, 14) || normalizeCell(leadRow?.[14]),
        continuesAs: firstNonEmptyEntryValue(entry.rows, 16) || normalizeCell(leadRow?.[16]),
        entryRowCount: rowValues.length,
        searchText: [lead.rowIndex + 1, transactionHash, pathLabel, systemNotes, rowValues.flat().join(' ')].join(' ').toLowerCase()
      };
    });
}

export function buildCaseDuplicateHashSearchText(item = {}) {
  return [
    item.itemKind,
    item.rowNumber,
    item.transactionHash,
    item.pathLabel,
    item.statusLabel,
    item.currentNotes,
    item.proposedNotes,
    item.attribution,
    item.analystNotes,
    item.fromHash,
    item.continuesAs,
    ...(item.otherPathLabels || [])
  ].map(value => normalizeCell(value).toLowerCase()).filter(Boolean).join(' ');
}

export function buildCaseDuplicateHashLabelerAnalysis(mainData = [], workflowState = {}, options = {}) {
  const caseId = normalizeCell(options.caseId) || 'case';
  const reviewState = workflowState?.duplicateHashLabelerReview && typeof workflowState.duplicateHashLabelerReview === 'object'
    ? workflowState.duplicateHashLabelerReview
    : {};
  const entries = buildCaseDuplicateHashEntryRecords(mainData);
  const groupsByHash = new Map();

  entries.forEach((record) => {
    if (!record.hashKey) return;
    if (!groupsByHash.has(record.hashKey)) groupsByHash.set(record.hashKey, []);
    groupsByHash.get(record.hashKey).push(record);
  });

  const duplicateGroups = [];
  const duplicateHashKeys = new Set();
  const candidates = [];

  groupsByHash.forEach((records, hashKey) => {
    if (!Array.isArray(records) || records.length <= 1) return;
    const labels = Array.from(new Set(records.map(record => record.pathLabel))).sort(duplicateHashNaturalCompare);
    if (labels.length <= 1) return;

    duplicateHashKeys.add(hashKey);
    duplicateGroups.push({
      hashKey,
      transactionHash: records[0]?.transactionHash || hashKey,
      pathLabels: labels,
      rowNumbers: records.map(record => record.rowNumber).sort((a, b) => a - b),
      entryCount: records.length
    });

    records.forEach((record) => {
      const otherPathLabels = labels.filter(label => label !== record.pathLabel);
      if (otherPathLabels.length === 0) return;
      const proposedNotes = buildCaseDuplicateHashProposedNotes(record.systemNotes, record.pathLabel, otherPathLabels);
      const reviewKey = buildCaseDuplicateHashReviewKey(caseId, record.rowIndex, record.transactionHash, record.pathLabel, proposedNotes);
      const isDifferent = normalizeCell(record.systemNotes) !== normalizeCell(proposedNotes);
      const proposalPreservesNotes = caseDuplicateHashProposalPreservesNonDuplicateNotes(record.systemNotes, proposedNotes);
      const isEligible = isDifferent && proposalPreservesNotes;
      const item = {
        itemKind: 'duplicate',
        itemKindLabel: 'Duplicate hash label',
        reviewKey,
        rowIndex: record.rowIndex,
        rowNumber: record.rowNumber,
        transactionHash: record.transactionHash,
        shortHash: abbreviateCaseBuddyHash(record.transactionHash),
        pathLabel: record.pathLabel,
        otherPathLabels,
        duplicatePathLabels: labels,
        duplicateRowNumbers: records.map(item => item.rowNumber).sort((a, b) => a - b),
        currentNotes: record.systemNotes,
        proposedNotes,
        strippedNotes: record.strippedNotes,
        attribution: record.attribution,
        analystNotes: record.analystNotes,
        fromHash: record.fromHash,
        continuesAs: record.continuesAs,
        entryRowCount: record.entryRowCount,
        isEligible,
        proposalPreservesNotes,
        statusLabel: !proposalPreservesNotes && isDifferent ? 'Manual review' : (isEligible ? 'Needs note refresh' : 'Current'),
        reason: !proposalPreservesNotes && isDifferent
          ? 'This proposal was blocked because it would change non-duplicate Column J notes. Review the row manually.'
          : (isEligible
            ? 'Column J needs the current duplicate-hash annotation after stripping any stale Tool 63 note.'
            : 'Column J already has the current duplicate-hash annotation.'),
        staleWasPresent: record.hasOldDuplicateNote,
        reviewBasisHash: `duplicate-hash-${caseBuddyHashString(JSON.stringify({ rowIndex: record.rowIndex, hash: record.hashKey, label: record.pathLabel, notes: record.systemNotes, proposedNotes }))}`
      };
      item.searchText = buildCaseDuplicateHashSearchText(item);
      candidates.push(item);
    });
  });

  entries.forEach((record) => {
    if (!record.hasOldDuplicateNote) return;
    if (duplicateHashKeys.has(record.hashKey)) return;
    const proposedNotes = stripCaseDuplicateHashNotes(record.systemNotes);
    if (normalizeCell(proposedNotes) === normalizeCell(record.systemNotes)) return;
    const proposalPreservesNotes = caseDuplicateHashProposalPreservesNonDuplicateNotes(record.systemNotes, proposedNotes);
    const reviewKey = buildCaseDuplicateHashReviewKey(caseId, record.rowIndex, record.transactionHash, record.pathLabel, proposedNotes);
    const item = {
      itemKind: 'stale_note',
      itemKindLabel: 'Stale duplicate note cleanup',
      reviewKey,
      rowIndex: record.rowIndex,
      rowNumber: record.rowNumber,
      transactionHash: record.transactionHash,
      shortHash: abbreviateCaseBuddyHash(record.transactionHash),
      pathLabel: record.pathLabel,
      otherPathLabels: [],
      duplicatePathLabels: [],
      duplicateRowNumbers: [],
      currentNotes: record.systemNotes,
      proposedNotes,
      strippedNotes: proposedNotes,
      attribution: record.attribution,
      analystNotes: record.analystNotes,
      fromHash: record.fromHash,
      continuesAs: record.continuesAs,
      entryRowCount: record.entryRowCount,
      isEligible: proposalPreservesNotes,
      proposalPreservesNotes,
      statusLabel: proposalPreservesNotes ? 'Stale note cleanup' : 'Manual review',
      reason: proposalPreservesNotes
        ? 'This row has an old duplicate-hash annotation, but the current Main Data no longer has this hash under differing path labels.'
        : 'This stale-note cleanup was blocked because it would change non-duplicate Column J notes. Review the row manually.',
      staleWasPresent: true,
      reviewBasisHash: `duplicate-hash-cleanup-${caseBuddyHashString(JSON.stringify({ rowIndex: record.rowIndex, hash: record.hashKey, label: record.pathLabel, notes: record.systemNotes, proposedNotes }))}`
    };
    item.searchText = buildCaseDuplicateHashSearchText(item);
    candidates.push(item);
  });

  duplicateGroups.sort((left, right) => String(left.transactionHash).localeCompare(String(right.transactionHash), undefined, { sensitivity: 'base' }));
  candidates.sort((left, right) => {
    if (left.isEligible !== right.isEligible) return left.isEligible ? -1 : 1;
    if (left.itemKind !== right.itemKind) return left.itemKind === 'stale_note' ? -1 : 1;
    const rowDiff = (left.rowNumber || 0) - (right.rowNumber || 0);
    if (rowDiff !== 0) return rowDiff;
    return duplicateHashNaturalCompare(left.pathLabel, right.pathLabel);
  });

  const eligibleItems = candidates.filter(item => item.isEligible);
  const blockedItems = candidates.filter(item => item.proposalPreservesNotes === false && normalizeCell(item.currentNotes) !== normalizeCell(item.proposedNotes));
  const staleNoteItems = candidates.filter(item => item.itemKind === 'stale_note');
  const duplicateItems = candidates.filter(item => item.itemKind === 'duplicate');
  const basisPayload = duplicateGroups.map(group => ({ hash: group.hashKey, labels: group.pathLabels, rows: group.rowNumbers }))
    .concat(candidates.map(item => ({ key: item.reviewKey, basis: item.reviewBasisHash, eligible: item.isEligible, blocked: item.proposalPreservesNotes === false })))
    .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  const currentBasisHash = `duplicate-hash-labeler-${caseBuddyHashString(JSON.stringify(basisPayload))}`;
  const completedBasisHash = normalizeCell(reviewState.completedBasisHash);
  const isCurrent = !!completedBasisHash && completedBasisHash === currentBasisHash && eligibleItems.length === 0 && blockedItems.length === 0;
  let summary = '';

  if (duplicateGroups.length === 0 && staleNoteItems.length === 0) {
    summary = 'No duplicate transaction hashes with differing path labels, and no stale duplicate-hash annotations were found.';
  } else if (blockedItems.length > 0) {
    summary = `${blockedItems.length} duplicate-hash note proposal${blockedItems.length === 1 ? '' : 's'} need manual review because they would change non-duplicate Column J notes.`;
  } else if (eligibleItems.length === 0) {
    summary = `${duplicateGroups.length} duplicate hash group${duplicateGroups.length === 1 ? '' : 's'} are currently documented; no duplicate-hash note updates remain.`;
  } else {
    summary = `${eligibleItems.length} duplicate-hash note update${eligibleItems.length === 1 ? '' : 's'} needed across ${duplicateGroups.length} duplicate group${duplicateGroups.length === 1 ? '' : 's'}${staleNoteItems.length ? `, including ${staleNoteItems.length} stale duplicate note cleanup${staleNoteItems.length === 1 ? '' : 's'}` : ''}.`;
  }

  return {
    entriesAnalyzed: entries.length,
    duplicateHashGroupCount: duplicateGroups.length,
    duplicateEntryCount: duplicateItems.length,
    eligibleEntryCount: eligibleItems.length,
    staleNoteCount: staleNoteItems.length,
    blockedProposalCount: blockedItems.length,
    currentItemCount: candidates.filter(item => !item.isEligible && item.proposalPreservesNotes !== false).length,
    summaryRows: duplicateGroups,
    duplicateGroups,
    candidates,
    items: candidates,
    eligibleItems,
    staleNoteItems,
    duplicateItems,
    blockedItems,
    currentBasisHash,
    completedBasisHash,
    isCurrent,
    pass: eligibleItems.length === 0 && blockedItems.length === 0,
    summary
  };
}

export function applyDuplicateHashLabelerItemsToMainMatrix(mainData = [], selectedItems = []) {
  const data = normalizeCaseMatrix(mainData);
  const items = (Array.isArray(selectedItems) ? selectedItems : []).filter(item => item?.reviewKey && item.isEligible);
  let rowsUpdated = 0;
  let duplicateNotesUpdated = 0;
  let staleNotesCleared = 0;
  let skippedCount = 0;
  let safetySkippedCount = 0;
  const updatedRows = [];
  const seenRows = new Set();

  items.forEach((item) => {
    const rowIndex = Number(item.rowIndex);
    if (!Number.isInteger(rowIndex) || rowIndex <= 0 || rowIndex >= data.length || seenRows.has(rowIndex)) {
      skippedCount += 1;
      return;
    }
    seenRows.add(rowIndex);
    const row = data[rowIndex];
    if (!Array.isArray(row)) {
      skippedCount += 1;
      return;
    }
    const currentHash = normalizeCell(row?.[1]);
    const currentNotes = normalizeCell(row?.[9]);
    if (duplicateHashNormalizeHash(currentHash) !== duplicateHashNormalizeHash(item.transactionHash) || currentNotes !== normalizeCell(item.currentNotes)) {
      skippedCount += 1;
      return;
    }
    const proposedNotes = normalizeCell(item.proposedNotes);
    if (currentNotes === proposedNotes) {
      skippedCount += 1;
      return;
    }
    if (!caseDuplicateHashProposalPreservesNonDuplicateNotes(currentNotes, proposedNotes)) {
      skippedCount += 1;
      safetySkippedCount += 1;
      return;
    }
    row[9] = proposedNotes;
    rowsUpdated += 1;
    if (item.itemKind === 'stale_note') staleNotesCleared += 1;
    else duplicateNotesUpdated += 1;
    updatedRows.push({
      rowIndex,
      rowNumber: rowIndex + 1,
      pathLabel: item.pathLabel,
      transactionHash: item.transactionHash,
      itemKind: item.itemKind,
      currentNotes,
      proposedNotes
    });
  });

  return {
    data,
    changed: rowsUpdated > 0,
    rowsUpdated,
    duplicateNotesUpdated,
    staleNotesCleared,
    skippedCount,
    safetySkippedCount,
    updatedRows
  };
}

export function buildDuplicateHashTableRows(analysis = {}) {
  const rows = [
    ['Hash', 'Path Labels', 'Entry Rows', 'Entry Count']
  ];
  const summaryRows = Array.isArray(analysis.summaryRows) ? analysis.summaryRows : [];
  if (summaryRows.length === 0) {
    rows.push(['No duplicate transaction hashes with differing path labels detected.', '', '', '']);
    return rows;
  }
  summaryRows.forEach((row) => {
    rows.push([
      row.transactionHash || '',
      (row.pathLabels || []).join(', '),
      (row.rowNumbers || []).join(', '),
      row.entryCount || (row.rowNumbers || []).length || ''
    ]);
  });
  return rows;
}

export function buildDuplicateHashLabelerAuditRows(analysis = {}, actionLabel = '') {
  const rows = [
    ['Case Buddy Duplicate Hash Labeler Audit', '', '', '', '', '', '', '', '', 'Generated by Label duplicate transaction hashes. Audit only; Main Data Column J remains the source of truth.'],
    ['Action', actionLabel || 'Review refreshed', 'Duplicate Groups', analysis.duplicateHashGroupCount || 0, 'Duplicate Entries', analysis.duplicateEntryCount || 0, 'Eligible Updates', analysis.eligibleEntryCount || 0, 'Stale Notes', analysis.staleNoteCount || 0],
    ['Status', analysis.summary || '', 'Basis', analysis.currentBasisHash || '', 'Completed Basis', analysis.completedBasisHash || '', '', '', '', ''],
    ['Kind', 'Status', 'Row', 'Path Label', 'Hash', 'Other Labels', 'Current Notes', 'Proposed Notes', 'Reason', 'Attribution']
  ];

  (analysis.candidates || []).forEach((item) => {
    rows.push([
      item.itemKindLabel || item.itemKind || '',
      item.statusLabel || '',
      item.rowNumber || '',
      item.pathLabel || '',
      item.transactionHash || '',
      (item.otherPathLabels || []).join(', '),
      item.currentNotes || '',
      item.proposedNotes || '',
      item.reason || '',
      item.attribution || ''
    ]);
  });

  rows.push([]);
  rows.push(['Duplicate Hash Table', '', '', '', '', '', '', '', '', '']);
  buildDuplicateHashTableRows(analysis).forEach(row => rows.push(row));
  return rows;
}
